import { defineComponent, reactive, onMounted, computed, PropType } from '@vue/composition-api';
import { enableCredits, getCreditInformation, disableCredits, addBoostingCredits, CreditAmount, setCreditReservationMaxPeriod } from '@/functions/credit-functions';
import { promotionsWithCredits } from '@/states/promotion-state';
import { Company } from '@/views/admin-companies/admin-companies-types';
import { getPromotionTitle } from '@/utilities/promotion-utilities';
import { i18n } from '@/utilities/vue-i18n';

export default defineComponent({
  props: {
    companyId: {
      type: Number,
      required: true
    },
    company: {
      type: Object as PropType<Company>,
      required: false
    }
  },
  emits: ['enabled', 'disabled'],
  setup(props, context) {
    const model = reactive({
      description: '',
      maxPeriodDays: 0,
      amounts: Array.from({ length: promotionsWithCredits.length }, () => 0) as number[],
    });

    const state = reactive({
      error: '',
      enabled: false,
      maxPeriodDays: 180,
      updatedAt: new Date(0),
      loading: false,
      loaded: false,
      amounts: [] as CreditAmount[],
    });

    const credits = computed<CreditAmount[]>(() => promotionsWithCredits.map(x => ({type: x, amount: state.amounts.find(y => y.type === x)?.amount ?? 0})));

    const getPromotionTitleForType = (promotionType: string) => {
      return getPromotionTitle(promotionType, i18n.t.bind(i18n), props.company?.volumePackageIsLight);
    };

    const loadCreditsInformation = async () => {
      state.loading = true;
      const information = await getCreditInformation(props.companyId);

      state.enabled = information.enabled;
      state.updatedAt = information.updatedAt;
      state.amounts = information.amounts;
      state.maxPeriodDays = information.maxPeriodDays;
      state.loading = false;
    };

    const enable = async () => {
      state.loading = true;
      try {
        await enableCredits({ companyId: props.companyId, description: model.description });
        await loadCreditsInformation();
        model.description = '';
        context.emit('enabled');
      }
      catch (err9r) {
        // Later?
      }
      state.loading = false;
    };

    const setMaxPeriod = async () => {
        state.loading = true;
        try {
            await setCreditReservationMaxPeriod({ companyId: props.companyId, maxPeriodDays: model.maxPeriodDays });
            await loadCreditsInformation();
        }
        catch (error) {
            // Later?
            model.maxPeriodDays = 0;
        }
        state.loading = false;
    };

    const disable = async () => {
      state.loading = true;
      try {
        await disableCredits(props.companyId);
        await loadCreditsInformation();
        context.emit('disabled');
      }
      catch (error) {
        // Later?
      }
      state.loading = false;
    };

    const addCredits = async () => {

      state.loading = true;

      const amounts = promotionsWithCredits.map((x, i) => ({type: x, amount: +model.amounts[i]})).filter(x => x.amount > 0);

      await addBoostingCredits({
        companyId: props.companyId,
        description: model.description,
        amount: amounts,
      });

      await loadCreditsInformation();
      
      model.amounts = Array.from({ length: promotionsWithCredits.length }, () => 0);

      model.description = '';
      state.loading = false;

    }

    onMounted(async () => {
      await loadCreditsInformation();
      model.maxPeriodDays = state.maxPeriodDays;
      state.loaded = true;
    });

    return {
      state,
      model,
      enable,
      disable,
      addCredits,
      credits,
      setMaxPeriod, // Expose setMaxPeriod for the template
      getPromotionTitle: getPromotionTitleForType,
    };
  }
});